import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import BusinessDetailsForm from './components/BusinessDetailsForm';
import SpecializationForm from './components/SpecializationForm';
import PaymentSetupForm from './components/PaymentSetupForm';
import { useAuth } from '../../contexts/AuthContext';


const AgentRegistration = () => {
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // ✅ NEW: Password fields
    password: '',
    confirmPassword: '',
    
    // Business Details
    companyName: '',
    registrationNumber: '',
    businessType: '',
    vatNumber: '',
    businessPhone: '',
    businessEmail: '',
    businessAddress: '',
    city: '',
    postcode: '',
    fullName: '',
    professionalTitle: '',
    experience: '',
    qualifications: '',
    
    // Specializations
    travelCategories: [],
    destinationContinents: [],
    destinationCountries: [],
    accommodationStandards: [],
    
    // Terms only (payment setup moved to agent settings)
    agreeToTerms: false,
    agreeToPrivacy: false,
    agreeToMarketing: false,
    understandFees: false
  });
  const [errors, setErrors] = useState({});
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  const totalSteps = 3;

  useEffect(() => {
    // Set page title
    document.title = 'Agent Registration - HoliYay';
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Business Details
        // ✅ NEW: Password validation
        if (!formData?.password) {
          newErrors.password = 'Password is required';
        } else if (formData?.password?.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        }
        
        if (!formData?.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData?.password !== formData?.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (!formData?.companyName) newErrors.companyName = 'Company name is required';
        if (!formData?.businessType) newErrors.businessType = 'Business type is required';
        if (!formData?.businessPhone) newErrors.businessPhone = 'Business phone is required';
        if (!formData?.businessEmail) newErrors.businessEmail = 'Business email is required';
        if (!formData?.businessAddress) newErrors.businessAddress = 'Business address is required';
        if (!formData?.city) newErrors.city = 'City is required';
        if (!formData?.postcode) newErrors.postcode = 'Postcode is required';
        if (!formData?.fullName) newErrors.fullName = 'Full name is required';
        if (!formData?.professionalTitle) newErrors.professionalTitle = 'Professional title is required';
        if (!formData?.experience) newErrors.experience = 'Experience level is required';
        
        if (formData?.businessEmail && !/\S+@\S+\.\S+/?.test(formData?.businessEmail)) {
          newErrors.businessEmail = 'Please enter a valid email address';
        }
        break;

      case 2: // Specializations
        if (!formData?.travelCategories || formData?.travelCategories?.length < 3) {
          newErrors.travelCategories = 'Please select at least 3 travel categories';
        }
        if (!formData?.destinationContinents || formData?.destinationContinents?.length === 0) {
          newErrors.destinationContinents = 'Please select at least one continent';
        }
        if (!formData?.accommodationStandards || formData?.accommodationStandards?.length === 0) {
          newErrors.accommodationStandards = 'Please select at least one accommodation standard';
        }
        break;

      case 3: // Terms & Conditions only
        if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms of service';
        if (!formData?.agreeToPrivacy) newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
        if (!formData?.understandFees) newErrors.understandFees = 'You must acknowledge the enquiry acceptance fee';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    
    try {
      // ✅ UPDATED: Use Supabase email+password signup
      const { data, error, requiresEmailConfirmation } = await signUp(
        formData?.businessEmail,
        formData?.password,
        {
          full_name: formData?.fullName,
          role: 'agent',
          company_name: formData?.companyName,
          phone: formData?.businessPhone,
          // Store agent profile data in user metadata for trigger to use
          agent_profile: {
            business_address: formData?.businessAddress,
            experience_years: formData?.experience,
            specializations: formData?.travelCategories,
            countries_operated: formData?.destinationCountries
          }
        }
      );

      if (error) {
        console.error('[Agent Registration] Error:', error);
        setErrors({ submit: error?.message || 'Registration failed. Please try again.' });
        return;
      }

      console.log('[Agent Registration] Success:', { userId: data?.user?.id, requiresEmailConfirmation });

      // ✅ UPDATED: Handle email confirmation flow
      if (requiresEmailConfirmation) {
        setShowEmailConfirmation(true);
      } else {
        // If no email confirmation required, agent is automatically logged in
        // Home.jsx will route them to /agent-vetting based on agent status
        window.location.href = '/';
      }
    } catch (error) {
      console.error('[Agent Registration] Unexpected error:', error);
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BusinessDetailsForm
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <SpecializationForm
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <PaymentSetupForm
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  // ✅ NEW: Email confirmation screen
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-4 py-16">
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Mail" size={32} className="text-success" />
              </div>
              
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Check Your Email
              </h2>
              
              <p className="text-muted-foreground mb-6">
                We've sent a confirmation email to <strong>{formData?.businessEmail}</strong>.
                Click the link in the email to verify your account and complete registration.
              </p>
              
              <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Next steps:</strong>
                </p>
                <ol className="text-sm text-muted-foreground text-left list-decimal list-inside space-y-2">
                  <li>Check your email inbox for the confirmation link</li>
                  <li>Click the confirmation link to verify your account</li>
                  <li>Sign in with your email and password</li>
                  <li>Complete the agent vetting process</li>
                </ol>
              </div>
              
              <Button
                variant="default"
                onClick={() => window.location.href = '/login'}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Go to Login
              </Button>
              
              <p className="text-xs text-muted-foreground mt-6">
                Didn't receive the email? Check your spam folder or contact support.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img
                src="/assets/images/No_Background-1763506485544.png"
                alt="HoliYay logo featuring a friendly fox mascot with Scottish heritage"
                className="h-10 w-auto"
              />
              <h1 className="text-3xl font-bold text-foreground">HoliYay</h1>
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Join as a Travel Agent</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Connect with customers seeking personalized travel experiences. Complete our verification process 
              to start accepting enquiries and growing your business through our marketplace.
            </p>
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

          {/* Form Content */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>
              )}
              
              <Button
                variant="ghost"
                onClick={() => window.location.href = '/customer-registration'}
                className="text-muted-foreground"
              >
                Sign in as Customer instead
              </Button>
            </div>

            <div className="flex items-center space-x-4">
              {currentStep < totalSteps ? (
                <Button
                  variant="default"
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  loading={loading}
                  iconName="Check"
                  iconPosition="left"
                >
                  Submit Application
                </Button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {errors?.submit && (
            <div className="mt-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-12 bg-muted/50 rounded-lg p-6">
            <div className="text-center">
              <Icon name="HelpCircle" size={32} className="mx-auto mb-3 text-muted-foreground" />
              <h3 className="font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our support team is here to help you through the registration process.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">support@holiyay.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">+44 20 1234 5678</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentRegistration;